import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IAuditWithUserInterface } from '../../../../global/audit.interface';
import { IPageableResponse } from '../../../../global/pagging.dto';
import { DocumentTypeData } from '../../contract-control/contract-control.dto';
import { DocumentTypeCustomBox, DocumentTypeRoot } from '../../documents/dto/contract-data.dto';
import { CustomColumns, IDocumentTypesDto } from '../../documents/dto/document-listing.dto';
import { IGetCategoriesItemDto } from '../../categories.dto';


export interface IFetchDocumentLayoutSettings{
    categories: number [];
    contract_control_columns: string;
    contract_control_preview: string;
    created_at: string;
    document_columns: string;
    document_preview: string;
    document_type_id: number;
    document_pinned_fields: string;
    id: number;
    updated_at: string;
    user_id: number;
    workspace_id: number;
}

export interface IFetchDocumentSectionLayoutSettings{
    categories: string;
    contract_control_columns: string;
    contract_control_preview: string;
    created_at: string;
    document_columns: string;
    document_preview: CustomColumns[];
    document_pinned_fields: string;
    document_type_id: number;
    id: number;
    updated_at: string;
    user_id: number;
    workspace_id: number;
}

export interface IUpdateLayoutSettingForm {
  section: string
  settings: string
}

export interface IUpdateDocumentTypeForm {
    id?: number,
    document_type: string
    selector_for: string
}

@Injectable({
  providedIn: 'root'
})
export class DocumentTypeService {

  public selectedDocumentType = new BehaviorSubject<IDocumentTypesDto | null>(null);

  constructor(private http:HttpClient) { }

  /**
   * Get document type with pagination and queryString
   * @param page
   * @param query
   * @param p
   * @param document_type_data
   * @returns response
   */
  getDocumentTypes(page: number | string, query: string, p: string = '', document_type_data: boolean = false){
    return this.http.get<IPageableResponse<IDocumentTypesDto> | IDocumentTypesDto[]>(`documenttypes?page=${page}&q=${query}&p=${p}&document_type_data=${document_type_data}`);
  }



  /**
   * Create document type
   * @param formData
   * @param extraParams
   * @returns response
   */
  submitDocumentTypes(formData: IUpdateLayoutSettingForm, extraParams: string = ''){
    return this.http.post<DocumentTypeRoot>(`documenttypes${extraParams}`, {
        ...formData,
    });
  }



  /**
   * Update document type selector
   * @param formData 
   * @returns 
   */
  updateDocumentTypeSelector(formData: {documentTypeId: number, selector_for: string}){
    return this.http.post(`documenttypes/${formData?.documentTypeId}/selector`, formData);
  }


  
  /**
   * Delete document type
   * @param id 
   * @returns response
   */
  deleteDocumentType(id: number){
    return this.http.delete(`documenttypes/${id}`);
  }


  /**
   * Make document type default
   * @param formData
   * @returns response
   */
  setDocumentTypeSelected(formData: {id: number, is_default: number}){
    return this.http.post(`documenttypes/${formData?.id}/isdefault`, formData);
  }

  /**
   * Deselect default document type
   * @param formData
   * @returns response
   */
  deSelectDefaultDocumentType(documentTypeId: number){
    return this.http.post(`documenttypes/${documentTypeId}/deselectdefault`,{});
  }

  

  /**
   * Retrieves a document type by its unique identifier.
   *
   * @param id - The unique identifier of the document type to retrieve.
   * @param extraParams - Optional additional query parameters to append to the request URL.
   * @returns An Observable that emits the document type data as an `IDocumentTypesDto`.
   */
  getDocumentTypeById(id: number, extraParams: string = ''): Observable<IDocumentTypesDto> {
    return this.http.get<IDocumentTypesDto>(`documenttypes/${id}${extraParams}`);
  }
 

  
  /**
   * get document layout setting
   * @param documentTypeId
   * @param layoutForm
   */
  saveDocumentLayoutSettings(documentTypeId:number, layoutForm): Observable<IDocumentTypesDto> {
    return this.http.post<IDocumentTypesDto>(`documenttypes/${documentTypeId}/document-settings`,layoutForm);
  }



  /**
   * get document layout setting
   * @param documentTypeId 
   */
  getDocumentLayoutSettings(documentTypeId){
    return this.http.get(`documenttypes/${documentTypeId}/document-settings`);
  }


  /**
   * update document type layout setting
   * @param documentTypeId
   * @param formData
   */
  updateDocumentLayoutSettings(documentTypeId:number, formData: IDocumentTypesDto){
    return this.http.post(`documenttypes/${documentTypeId}/layout-settings`,formData);
  }



  /**
   * Retrieves the layout settings for a specific document type.
   *
   * @param documentTypeId - The unique identifier of the document type whose layout settings are to be fetched.
   * @returns An Observable emitting the layout settings for the specified document type.
   */
  fetchDocumentLayoutSettings(documentTypeId: number): Observable<IDocumentTypesDto> {
    return this.http.get<IDocumentTypesDto>(`documenttypes/${documentTypeId}/layout-settings`);
  }


  
  /**
   * get document section layout setting
   * @param documentTypeId 
   */
  fetchDocumentSectionLayoutSettings(documentTypeId:number){
    return this.http.get<IFetchDocumentSectionLayoutSettings>(`documenttypes/${documentTypeId}/document-layout-settings`);
  }


  setSelectedDocumentType(documentType: IDocumentTypesDto): void {
    this.selectedDocumentType.next(documentType);
  }
 
  getSelectedDocumentType() {
    return this.selectedDocumentType.asObservable();
  }
}

export interface IGetDocumentTypeByIdResponse extends  IAuditWithUserInterface{
    id:number,
    workspace_id:number,
    document_type:string,
    is_default:boolean
}


export interface IGetDocumentTypeByIdDetailsResponse extends  IGetDocumentTypeByIdResponse{
    document_type_data:DocumentTypeCustomBox
}

export interface ISendDocumentType extends IAuditWithUserInterface{
    document_type_data: DocumentTypeData;
    id: number;
    workspace_id: number;
    document_type: string;
    is_default: boolean;
}
